import { BaseElement } from 'base';
import Swiper from 'swiper-core';

const REDUCE = '(prefers-reduced-motion: reduce)';
const DESKTOP = '(min-width: 1200px)';
const SLIDE_SPEED = 300;

class BeforeAfterCarousel extends BaseElement {
  #swiper = null;
  #viewport = null;
  #nav = null;
  #dots = [];
  #live = null;
  #reduceMq = null;
  #resizeIndex = null;

  onConnect() {
    this.#viewport = this.q('[data-viewport]');
    if (!this.#viewport) return;

    this.#nav = this.q('.before-after__nav');
    this.#dots = this.qa('[data-dot]');
    this.#live = this.q('[data-live]');

    this.#reduceMq = window.matchMedia(REDUCE);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);
    this.listen(window.matchMedia(DESKTOP), 'change', this.#onDesktopChange);
    this.listen(this.#viewport, 'keydown', this.#onKeydown);
    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'shopify:block:select', this.#onBlockSelect);
    this.listen(window, 'resize', this.#onWindowResize);
    this.cleanup(() => this.#unmount());

    this.#initSwiper();
    this.#bindArrows();
  }

  #unmount() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #initSwiper() {
    if (!this.#viewport || this.#count() < 2) return;
    const mode = this.#navMode();
    this.#swiper = new Swiper(this.#viewport, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: this.#readNumber('--before-after-pair-gap'),
      loop: mode.loop,
      rewind: mode.rewind,
      loopAdditionalSlides: mode.extra,
      watchOverflow: true,
      watchSlidesProgress: true,
      speed: this.#slideSpeed(),
      touchEventsTarget: 'container',
      on: {
        afterInit: instance => {
          this.#syncInert(instance);
          this.#syncDots(instance);
          this.#syncArrows(instance);
        },
        progress: instance => this.#syncInert(instance),
        slideChange: instance => this.#syncDots(instance),
        slideChangeTransitionEnd: instance => this.#announce(instance),
        lock: instance => {
          this.#syncInert(instance);
          this.#syncDots(instance);
          this.#syncArrows(instance);
        },
        unlock: instance => {
          this.#syncInert(instance);
          this.#syncDots(instance);
          this.#syncArrows(instance);
        },
        beforeResize: instance => {
          this.#resizeIndex = instance.realIndex;
        },
        resize: instance => this.#onResize(instance)
      }
    });
  }

  #navMode() {
    const count = this.#count();
    const perView = this.#perView();
    if (count >= perView + 2)
      return {
        loop: true,
        rewind: false,
        extra: count >= perView + 4 ? 1 : 0
      };
    if (count > perView) return { loop: false, rewind: true, extra: 0 };
    return { loop: false, rewind: false, extra: 0 };
  }

  #perView() {
    return this.#readNumber('--before-after-per-view') || 1;
  }

  async #bindArrows() {
    const previousButton = this.q('action-button.before-after__arrow--prev');
    const nextButton = this.q('action-button.before-after__arrow--next');
    if (!previousButton && !nextButton) return;

    let active = true;
    this.cleanup(() => {
      active = false;
      previousButton?.run?.(null);
      nextButton?.run?.(null);
    });

    await customElements.whenDefined('action-button');
    if (!active || !this.isConnected) return;
    previousButton?.run(() => this.#step(-1));
    nextButton?.run(() => this.#step(1));
  }

  #syncArrows(instance) {
    const locked = instance.isLocked;
    if (locked) this.#releaseArrowFocus();
    this.toggleAttribute('data-arrows', !locked);
  }

  #releaseArrowFocus() {
    if (this.#nav?.contains(document.activeElement))
      this.#viewport.focus({ preventScroll: true });
  }

  #onDesktopChange(event) {
    if (!event.matches) this.#releaseArrowFocus();
  }

  #step(direction) {
    const instance = this.#swiper;
    if (!instance || instance.isLocked) return;
    if (direction > 0) instance.slideNext();
    else instance.slidePrev();
  }

  #onWindowResize() {
    this.#onResize();
  }

  #onResize(instance = this.#swiper) {
    if (!instance || instance.destroyed) return;
    const before = this.#resizeIndex;
    this.#resizeIndex = null;
    const mode = this.#navMode();
    if (
      instance.params.loop !== mode.loop ||
      instance.params.loopAdditionalSlides !== mode.extra
    ) {
      const index = before ?? instance.realIndex;
      queueMicrotask(() => {
        if (!this.isConnected || instance !== this.#swiper) return;
        this.#swiper.destroy(true, true);
        this.#swiper = null;
        this.#initSwiper();
        this.#goTo(index, 0, false);
      });
      return;
    }
    instance.params.rewind = mode.rewind;
    if (before !== null && before !== instance.realIndex)
      this.#goTo(before, 0, false);
    this.#syncInert(instance);
  }

  #goTo(index, speed, runCallbacks = true) {
    const instance = this.#swiper;
    if (!instance || instance.destroyed) return;
    if (instance.params.loop) instance.slideToLoop(index, speed, runCallbacks);
    else instance.slideTo(index, speed, runCallbacks);
  }

  #syncInert(instance) {
    const visibleClass = instance.params.slideVisibleClass;
    const focused = document.activeElement;
    for (const slide of instance.slides) {
      const keep = focused ? slide.contains(focused) : false;
      slide.toggleAttribute(
        'inert',
        !keep && !slide.classList.contains(visibleClass)
      );
    }
  }

  #syncDots(instance) {
    if (!this.#dots.length) return;
    const index = instance.realIndex;
    for (const dot of this.#dots) {
      if (Number(dot.dataset.index) === index)
        dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  #announce(instance) {
    if (!this.#live || !this.contains(document.activeElement)) return;
    const dot = this.#dots[instance.realIndex];
    this.#live.textContent = dot?.getAttribute('aria-label') ?? '';
  }

  #slideSpeed() {
    return this.#reduceMq?.matches ? 0 : SLIDE_SPEED;
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
  }

  #onClick(event) {
    const dot = event.target.closest?.('[data-dot]');
    if (!dot || !this.contains(dot)) return;
    this.#goTo(Number(dot.dataset.index) || 0);
  }

  #onKeydown(event) {
    if (event.target !== this.#viewport) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const instance = this.#swiper;
    if (!instance || instance.isLocked) return;
    event.preventDefault();
    const forward = instance.rtlTranslate
      ? event.key === 'ArrowLeft'
      : event.key === 'ArrowRight';
    this.#step(forward ? 1 : -1);
  }

  #onBlockSelect(event) {
    const pair = event.target.closest?.('[data-pair]');
    if (!pair || !this.contains(pair)) return;
    this.#goTo(Number(pair.dataset.index) || 0, 0);
  }

  #count() {
    return Number(this.dataset.count) || 0;
  }

  #readNumber(name) {
    return parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0;
  }
}

if (!customElements.get('before-after-carousel')) {
  customElements.define('before-after-carousel', BeforeAfterCarousel);
}
